import React, {
    createContext, useReducer, useMemo, useEffect, useContext,
} from 'react';
import { ActiveFarmContext } from '../ActiveFarm';
import GET_META_GROUP from './getMetaGroup.query.js';
import GET_HERD_VISIBILITY_USER_SETTINGS from '../../pages/Herd/HerdSettings/getHerdVisibilityUserSettings.query';
import useLazyGetData from '../../customHooks/useLazyGetData';


const initialState = {
    groups: {
        ZelpAlerts: [],
        myGroups: [],
        automaticLists: [],
        suggestedGroups: [],
        suggestedLists: [],
        allAnimals: []
    },
    farmName: null,
    error: null,
    errorCode: null,
    isGroupsLoading: true,
    isAddGroupOpen: false,
    newGroupId: null,
    newGroupFields: {
        name: '',
        description: '',
        animals: []
    },
    herdVisibilitySettings: null,
}

const actions = {
    SET_GROUPS: 'SET_GROUPS',
    SET_FARM_NAME: 'SET_FARM_NAME',
    SET_ERROR: 'SET_ERROR',
    SET_ERROR_CODE: 'SET_ERROR_CODE',
    SET_IS_GROUPS_LOADING: 'SET_IS_GROUPS_LOADING',
    SET_IS_ADD_GROUP_OPEN: 'SET_IS_ADD_GROUP_OPEN',
    SET_NEW_GROUP_ID: 'SET_NEW_GROUP_ID',
    SET_NEW_GROUP_NAME: 'SET_NEW_GROUP_NAME',
    SET_NEW_GROUP_DESCRIPTION: 'SET_NEW_GROUP_DESCRIPTION',
    ADD_ANIMAL_INTO_NEW_GROUP: 'ADD_ANIMAL_INTO_NEW_GROUP',
    REMOVE_ANIMAL_FROM_NEW_GROUP: 'ADD_ANIMAL_INTO_NEW_GROUP',
    SET_HERD_VISIBILITY_SETTINGS: 'SET_HERD_VISIBILITY_SETTINGS',
}

function reducer(state, action) {
    switch (action.type) {
        case actions.SET_GROUPS:
            return { ...state, groups: { ...state.groups, [action.groupName]: action.groupValue } };
        case actions.SET_FARM_NAME:
            return { ...state, farmName: action.payload };
        case actions.SET_ERROR:
            return { ...state, error: action.payload };
        case actions.SET_ERROR_CODE:
            return { ...state, errorCode: action.payload };
        case actions.SET_IS_GROUPS_LOADING:
            return { ...state, isGroupsLoading: action.payload };
        case actions.SET_IS_ADD_GROUP_OPEN:
            return { ...state, isAddGroupOpen: action.payload };
        case actions.SET_NEW_GROUP_ID:
            return { ...state, newGroupId: action.payload };
        case actions.SET_NEW_GROUP_NAME:
            return { ...state, newGroupFields: { ...state.newGroupFields, name: action.payload } };
        case actions.SET_NEW_GROUP_DESCRIPTION:
            return { ...state, newGroupFields: { ...state.newGroupFields, description: action.payload } };
        case actions.ADD_ANIMAL_INTO_NEW_GROUP:
            return {
                ...state,
                newGroupFields: {
                    ...state.newGroupFields,
                    animals: [...state.newGroupFields.animals, action.payload]
                }
            };
        case actions.REMOVE_ANIMAL_FROM_NEW_GROUP:
            return {
                ...state,
                newGroupFields: {
                    ...state.newGroupFields,
                    animals: state.newGroupFields.animals.filter(animal => animal !== action.payload)
                }
            };
        case actions.SET_HERD_VISIBILITY_SETTINGS:
            return { ...state, herdVisibilitySettings: action.payload };
        default:
            return state;
    }
}

export const HerdContext = createContext();

const HerdContextProvider = ({ children }) => {

    const [state, dispatch] = useReducer(reducer, initialState);

    const [getMetaGroup, {
        data, loading, error, errorCode
    }] = useLazyGetData(GET_META_GROUP, null, true, true);

    const [getVisibilitySettings, {
        data: visibilityData, loading: visibilityLoading,
    }] = useLazyGetData(GET_HERD_VISIBILITY_USER_SETTINGS, null, true);

    const { activeFarm } = useContext(ActiveFarmContext);

    useEffect(() => {
        if (!visibilityLoading && visibilityData) {
            const savedSettings = visibilityData?.saved_settings;
            if (Array.isArray(savedSettings)) {
                const map = {};
                savedSettings.forEach((entry) => {
                    map[entry.id] = entry.isVisible;
                });
                dispatch({ type: actions.SET_HERD_VISIBILITY_SETTINGS, payload: map });
            } else {
                dispatch({ type: actions.SET_HERD_VISIBILITY_SETTINGS, payload: {} });
            }
        }
    }, [visibilityData, visibilityLoading]);

    useEffect(() => {
        if (!loading && !error && data) {
            dispatch({
                type: actions.SET_GROUPS,
                groupName: 'AutomaticLists',
                groupValue: data?.farmSummary?.AutomaticLists
            });
            dispatch({
                type: actions.SET_GROUPS,
                groupName: 'MyGroups',
                groupValue: data?.farmSummary?.MyGroups
            });
            dispatch({
                type: actions.SET_GROUPS,
                groupName: 'AllAnimals',
                groupValue: data?.farmSummary?.AllAnimals
            });
            dispatch({
                type: actions.SET_GROUPS,
                groupName: 'ZelpAlerts',
                groupValue: data?.farmSummary?.ZelpAlerts
            });
            dispatch({
                type: actions.SET_GROUPS,
                groupName: 'SuggestedGroups',
                groupValue: data?.farmSummary?.SuggestedGroups
            });
            dispatch({
                type: actions.SET_GROUPS,
                groupName: 'SuggestedLists',
                groupValue: data?.farmSummary?.SuggestedLists
            });
            dispatch({
                type: actions.SET_FARM_NAME,
                payload: data?.farm?.name
            });
            dispatch({ type: actions.SET_ERROR, payload: null });
            dispatch({ type: actions.SET_ERROR_CODE, payload: null });
            dispatch({ type: actions.SET_IS_GROUPS_LOADING, payload: false });
        }
        else if (!loading && error) {
            dispatch({ type: actions.SET_ERROR, payload: error });
            dispatch({ type: actions.SET_ERROR_CODE, payload: errorCode });
            dispatch({ type: actions.SET_IS_GROUPS_LOADING, payload: false });
        }
    }, [data, loading, error, errorCode]);

    useEffect(() => {
        const farmId = Number(activeFarm);
        if (farmId > 0) {
            getMetaGroup({ farmId });
            getVisibilitySettings({ farmId });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFarm]);

    const value = useMemo(() => ({
        groups: state.groups,
        farmName: state.farmName,
        error: state.error,
        errorCode: state.errorCode,
        herdVisibilitySettings: state.herdVisibilitySettings,
        setGroups: (groupName, groupValue) => { dispatch({ type: actions.SET_GROUPS, groupName, groupValue }) },
        updateGroupsList: () => {
            const farmId = Number(activeFarm);
            if (farmId > 0) {
                getMetaGroup({ farmId });
                getVisibilitySettings({ farmId });
            }
        },
        isGroupsLoading: state.isGroupsLoading,
        setIsGroupsLoading: payload => { dispatch({ type: actions.SET_IS_GROUPS_LOADING, payload }) },
        isAddGroupOpen: state.isAddGroupOpen,
        setIsAddGroupOpen: payload => { dispatch({ type: actions.SET_IS_ADD_GROUP_OPEN, payload }) },
        newGroupId: state.newGroupId,
        setNewGroupId: payload => { dispatch({ type: actions.SET_NEW_GROUP_ID, payload }) },
        newGroupFields: state.newGroupFields,
        setNewGroupName: payload => { dispatch({ type: actions.SET_NEW_GROUP_NAME, payload }) },
        setNewGroupDescription: payload => { dispatch({ type: actions.SET_NEW_GROUP_DESCRIPTION, payload }) },
        addAnimalIntoNewGroup: payload => { dispatch({ type: actions.ADD_ANIMAL_INTO_NEW_GROUP, payload }) },
        removeAnimalFromNewGroup: payload => { dispatch({ type: actions.REMOVE_ANIMAL_FROM_NEW_GROUP, payload }) },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [state, dispatch]);

    return (
        <HerdContext.Provider value={value}>
            {children}
        </HerdContext.Provider>
    );
}

export default HerdContextProvider;
